import type { Dosage, Timing } from "@medplum/fhirtypes";
import type { DoseFrequency, DoseUnit, MedicationDosage, MedicationRoute } from "@repo/domain";
import { SNOMED_SYSTEM, TIMING_ABBREVIATION_SYSTEM, UCUM_SYSTEM } from "@repo/fhir-systems";

/**
 * MedicationDosage ↔ the first FHIR Dosage, shared by the engine codec and the
 * data store's read side so the two cannot spell a code differently.
 *
 * The domain owns four elements: doseAndRate, timing, route and asNeededBoolean.
 * A prior element this mapping cannot read (a Q3H timing, a text-only route) is
 * kept on write, because the domain never saw it and so cannot have cleared it.
 * One it CAN read and the domain no longer carries was cleared, and is dropped.
 */

type Repeat = NonNullable<Timing["repeat"]>;

const FREQUENCIES: Record<DoseFrequency, { repeat: Repeat; abbreviation?: string }> = {
  daily: { repeat: { frequency: 1, period: 1, periodUnit: "d" }, abbreviation: "QD" },
  "twice-daily": { repeat: { frequency: 2, period: 1, periodUnit: "d" }, abbreviation: "BID" },
  "three-times-daily": { repeat: { frequency: 3, period: 1, periodUnit: "d" }, abbreviation: "TID" },
  "four-times-daily": { repeat: { frequency: 4, period: 1, periodUnit: "d" }, abbreviation: "QID" },
  "every-4-hours": { repeat: { frequency: 1, period: 4, periodUnit: "h" }, abbreviation: "Q4H" },
  "every-6-hours": { repeat: { frequency: 1, period: 6, periodUnit: "h" }, abbreviation: "Q6H" },
  "every-8-hours": { repeat: { frequency: 1, period: 8, periodUnit: "h" }, abbreviation: "Q8H" },
  "every-other-day": { repeat: { frequency: 1, period: 2, periodUnit: "d" }, abbreviation: "QOD" },
  "at-bedtime": { repeat: { frequency: 1, period: 1, periodUnit: "d", when: ["HS"] } },
  weekly: { repeat: { frequency: 1, period: 1, periodUnit: "wk" } },
  monthly: { repeat: { frequency: 1, period: 1, periodUnit: "mo" } },
};

const ROUTES: Record<MedicationRoute, { code: string; display: string }> = {
  oral: { code: "26643006", display: "Oral route" },
  sublingual: { code: "37839007", display: "Sublingual route" },
  topical: { code: "6064005", display: "Topical route" },
  transdermal: { code: "45890007", display: "Transdermal route" },
  inhaled: { code: "447694001", display: "Respiratory tract route" },
  nasal: { code: "46713006", display: "Nasal route" },
  ophthalmic: { code: "54485002", display: "Ophthalmic route" },
  otic: { code: "10547007", display: "Otic route" },
  rectal: { code: "37161004", display: "Rectal route" },
  vaginal: { code: "16857009", display: "Vaginal route" },
  subcutaneous: { code: "34206005", display: "Subcutaneous route" },
  intramuscular: { code: "78421000", display: "Intramuscular route" },
  intravenous: { code: "47625008", display: "Intravenous route" },
};

/** Counted forms are UCUM annotations, which UCUM defines as the unity `1`. */
const UNITS: Record<DoseUnit, string> = {
  tablet: "{tablet}",
  capsule: "{capsule}",
  mg: "mg",
  mcg: "ug",
  g: "g",
  mL: "mL",
  drop: "[drp]",
  puff: "{puff}",
  patch: "{patch}",
  application: "{application}",
  unit: "[iU]",
};

function sameRepeat(a: Repeat, b: Repeat): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof Repeat>;
  return [...keys].every((k) => JSON.stringify(a[k]) === JSON.stringify(b[k]));
}

function readAmount(d: Dosage): MedicationDosage["amount"] {
  if (d.doseAndRate?.length !== 1) return undefined;
  const q = d.doseAndRate[0]?.doseQuantity;
  if (q?.system !== UCUM_SYSTEM || q.value === undefined || !(q.value > 0)) return undefined;
  const unit = (Object.keys(UNITS) as DoseUnit[]).find((u) => UNITS[u] === q.code);
  return unit ? { value: q.value, unit } : undefined;
}

function readFrequency(d: Dosage): DoseFrequency | undefined {
  const t = d.timing;
  if (!t?.repeat || t.event) return undefined;
  const repeat = t.repeat;
  return (Object.keys(FREQUENCIES) as DoseFrequency[]).find((f) => sameRepeat(FREQUENCIES[f].repeat, repeat));
}

function readRoute(d: Dosage): MedicationRoute | undefined {
  const coding = d.route?.coding?.find((c) => c.system === SNOMED_SYSTEM);
  return (Object.keys(ROUTES) as MedicationRoute[]).find((r) => ROUTES[r].code === coding?.code);
}

function writeAmount(amount: NonNullable<MedicationDosage["amount"]>): Dosage["doseAndRate"] {
  return [{ doseQuantity: { value: amount.value, unit: amount.unit, system: UCUM_SYSTEM, code: UNITS[amount.unit] } }];
}

function writeFrequency(frequency: DoseFrequency): Timing {
  const { repeat, abbreviation } = FREQUENCIES[frequency];
  return {
    ...(abbreviation ? { code: { coding: [{ system: TIMING_ABBREVIATION_SYSTEM, code: abbreviation }] } } : {}),
    repeat: structuredClone(repeat),
  };
}

function writeRoute(route: MedicationRoute): Dosage["route"] {
  return { coding: [{ system: SNOMED_SYSTEM, ...ROUTES[route] }] };
}

export function medicationDosageFromFhir(dosage: Dosage | undefined): MedicationDosage | undefined {
  if (!dosage) return undefined;
  const amount = readAmount(dosage);
  const frequency = readFrequency(dosage);
  const route = readRoute(dosage);
  const out: MedicationDosage = {
    ...(amount ? { amount } : {}),
    ...(frequency ? { frequency } : {}),
    ...(route ? { route } : {}),
    ...(dosage.asNeededBoolean === true ? { asNeeded: true } : {}),
  };
  return Object.keys(out).length ? out : undefined;
}

export function medicationDosageToFhir(
  dosage: MedicationDosage | undefined,
  text: string | undefined,
  prior: Dosage | undefined,
): Dosage | undefined {
  const next: Dosage = { ...prior };
  delete next.text;
  if (text) next.text = text;

  // Each owned element: write the domain's value, else drop it only if we could read it.
  if (dosage?.amount) next.doseAndRate = writeAmount(dosage.amount);
  else if (prior && readAmount(prior)) delete next.doseAndRate;

  if (dosage?.frequency) next.timing = writeFrequency(dosage.frequency);
  else if (prior && readFrequency(prior)) delete next.timing;

  if (dosage?.route) next.route = writeRoute(dosage.route);
  else if (prior && readRoute(prior)) delete next.route;

  if (dosage?.asNeeded) next.asNeededBoolean = true;
  else if (prior?.asNeededBoolean === true) delete next.asNeededBoolean;

  return Object.keys(next).length ? next : undefined;
}
